import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material/";
import { useSelector } from "react-redux";
import { useErrorBoundary } from "react-error-boundary";
import InfiniteScroll from "react-infinite-scroll-component";
import useIsFirstRender from "../../../hooks/useIsFirstRender";
import Ghost from "../../../views/ghost/Ghost";
import { GhostTemplates } from "../../../enums/GhostTemplates";
import Tips from "../../../views/tips/Tips";
import { TipFilters as TipFiltersInterface } from "../../../interfaces/TipFilters";
import TipFilters from "../../../views/filters/TipFilters";
import { RootState } from "../../../../store";
import TipFiltersChips from "../../../views/filters-chips/TipFiltersChips";
import TipsService from "../../../services/tips.service";
import { Tip } from "../../../interfaces/Tip";
import { TipsResponse } from "../../../interfaces/TipsResponse";
import TipPreferencesDropdown from "../../preferences-dropdown/TipPreferencesDropdown";

const TIPS_PER_LOAD = 7;
const END_MESSAGE = "Yay! You have seen all the tips";
const NO_TIPS_MESSAGE = "No tips available yet.";

const AllTips = () => {
  const { showBoundary } = useErrorBoundary();
  const [tips, setTips] = useState<Tip[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [areTipsLoading, setAreTipsLoading] = useState<boolean>(false);
  const tipFilters = useSelector(
    (state: RootState) => state.tipFilters.tipFilters
  );
  const prevFilters = useRef<TipFiltersInterface>(tipFilters);
  const firstRender = useIsFirstRender();

  const fetchTips = useCallback(
    (page: number) => {
      setAreTipsLoading(true);
      TipsService.getFilteredWithPagination(page, TIPS_PER_LOAD, tipFilters)
        .then((response: TipsResponse) => {
          if (response.data.length < TIPS_PER_LOAD) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }

          setTips((prevTips) =>
            page === 1 ? response.data : prevTips.concat(response.data)
          );
        })
        .catch((error: Error) => {
          console.error("Failed to fetch tips:", error);
          setHasMore(false);
          showBoundary(error);
        })
        .finally(() => setAreTipsLoading(false));
    },
    [tipFilters, showBoundary]
  );

  const areFiltersEmpty = useMemo(
    () =>
      Object.values(tipFilters).every(
        (value) =>
          value === null || (Array.isArray(value) && value.length === 0)
      ),
    [tipFilters]
  );

  useEffect(() => {
    if (firstRender) {
      fetchTips(1);
      prevFilters.current = tipFilters;

      return;
    }

    if (JSON.stringify(prevFilters.current) !== JSON.stringify(tipFilters)) {
      setPageNumber(1);
      setHasMore(true);
      fetchTips(1);
      prevFilters.current = tipFilters;
    }
  }, [tipFilters, fetchTips, firstRender]);

  const handleLoadMore = useCallback(() => {
    if (!areTipsLoading && hasMore) {
      const nextPage = pageNumber + 1;
      fetchTips(nextPage);
      setPageNumber(nextPage);
    }
  }, [fetchTips, pageNumber, areTipsLoading, hasMore]);

  return (
    <Box className="pb-3">
      <Box className="d-flex flex-column flex-sm-row justify-content-between">
        <TipFilters />
        <TipPreferencesDropdown />
      </Box>
      <TipFiltersChips filters={tipFilters} />
      {areTipsLoading && (
        <Ghost repeatValue={5} template={GhostTemplates.TIP_CARD} />
      )}
      <InfiniteScroll
        dataLength={tips.length}
        next={handleLoadMore}
        hasMore={hasMore}
        loader={<Ghost repeatValue={5} template={GhostTemplates.TIP_CARD} />}
        endMessage={
          tips.length > 0 ? (
            <Typography align="center" className="h3">
              {END_MESSAGE}
            </Typography>
          ) : null
        }
      >
        <Tips
          tips={tips}
          areFiltersEmpty={areFiltersEmpty}
          loading={areTipsLoading}
        />
      </InfiniteScroll>
      {!areTipsLoading && tips.length === 0 && areFiltersEmpty && (
        <Typography align="center" className="h4 mt-4">
          {NO_TIPS_MESSAGE}
        </Typography>
      )}
    </Box>
  );
};

export default AllTips;
